import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getWaitlistProgramNames } from '../ConvertToApplicantModal/utils/programs';
import { resolveFloorPlanDisplay } from '../../utils/floorPlan';
import {
  isWaitlistOnlyUnit,
  shouldRestrictByFloorPlan,
  shouldRestrictToNonWaitlistPrograms,
} from './utils';

const UNRESTRICTED = (parsedResults) => ({
  restrictedResults: parsedResults,
  restriction: null,
});

/**
 * Decides which available units an applicant may be assigned, and why.
 *
 * Both rules fail open: a waitlist configuration or waitlist record that failed
 * to load yields no program names and no positions, so nothing is hidden. An
 * empty unit list under a banner nobody can act on reads as a broken page,
 * while the row-level disable in getIsUnitAssignDisabled still guards the
 * floor plan case.
 */
const useAssignmentRestrictions = ({
  parsedResults,
  applicantToAssignUnit,
  waitlistConfiguration,
  waitlistPositions,
  floorPlans,
  isTransfer,
}) => {
  const { affordableWaitlistV2: restrictionsEnabled = false } = useFlags();

  const preferredFloorPlanId =
    applicantToAssignUnit?.prospectInfo?.prospectPreferences
      ?.preferredFloorPlanId ?? null;
  const applicationStatusName = applicantToAssignUnit?.applicationStatus?.name;

  return useMemo(() => {
    if (!restrictionsEnabled || isTransfer) {
      return UNRESTRICTED(parsedResults);
    }

    const restrictByFloorPlan = shouldRestrictByFloorPlan({
      preferredFloorPlanId,
    });

    const waitlistProgramNames = getWaitlistProgramNames(waitlistConfiguration);
    const restrictByProgram =
      shouldRestrictToNonWaitlistPrograms({
        waitlistPositions: waitlistPositions ?? [],
        applicationStatusName,
      }) && waitlistProgramNames.length > 0;

    if (!restrictByFloorPlan && !restrictByProgram) {
      return UNRESTRICTED(parsedResults);
    }

    const restrictedResults = parsedResults.filter((result) => {
      if (
        restrictByFloorPlan &&
        result.originalUnit?.floorPlanId !== preferredFloorPlanId
      ) {
        return false;
      }

      return !(
        restrictByProgram &&
        isWaitlistOnlyUnit({
          fpPap: result.fpPap ?? [],
          waitlistProgramNames,
        })
      );
    });

    const floorPlanName = restrictByFloorPlan
      ? resolveFloorPlanDisplay({
          floorPlans,
          unitFloorPlanId: null,
          preferredFloorPlanId,
        }).name
      : null;

    // The floor-plan filter runs off preferredFloorPlanId without waiting on floorPlans,
    // so the name can be unresolved either because that query failed or because the id
    // points at a floor plan the property no longer offers (nothing nulls the prospect
    // preference when a plan is soft-deleted). With no name there is no honest banner —
    // the "waitlist configuration" copy would misname the reason — so when this is the
    // only active rule, fail open rather than narrow the list without explaining it. A
    // stale id matches no unit, so filtering here would empty the table silently. The
    // row-level disable in getIsUnitAssignDisabled covers exactly this rule behind the
    // same flag, and still blocks the assignment with a per-row tooltip.
    // When the program rule is also active, the no-floor-plan copy stays truthful.
    if (restrictByFloorPlan && !floorPlanName && !restrictByProgram) {
      return UNRESTRICTED(parsedResults);
    }

    return { restrictedResults, restriction: { floorPlanName } };
  }, [
    restrictionsEnabled,
    applicationStatusName,
    floorPlans,
    isTransfer,
    parsedResults,
    preferredFloorPlanId,
    waitlistConfiguration,
    waitlistPositions,
  ]);
};

export default useAssignmentRestrictions;
